import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import cn from 'classnames';
import { PillDropdown } from '@/shared/ui/PillDropdown';
import { pillDropdownItemClassName } from '@/shared/ui/pillDropdownItemClassName';
import type { WalletOption } from '../model/types';

// Вариант ProfitInfo для строки аккаунта (layout.header = 'in-content', макет
// telecom): белая пилюля «Баланс 3 284,60 ₽» справа над контентом. Сумма — тот же
// основной кошелёк, что в остальных вариантах (рублёвый, если есть). По клику —
// список всех кошельков, как «Кошельки» у wapclick: строка ведёт на Финансы с
// этим кошельком в query.
//
// «За сегодня» в макете нет, поэтому здесь его и не показываем.

type ProfitInfoPillProps = {
  userBalanceAmount: string;
  wallets: WalletOption[];
  // Верхняя панель телефона (compactNav = 'rail-topbar'): тёмная маленькая
  // пилюля с одной суммой, без слова «Баланс» — места в панели мало. Список
  // кошельков тот же, что на десктопе (в макете его на телефоне не было, 23.09).
  compact?: boolean;
  className?: string;
};

export const ProfitInfoPill = ({ userBalanceAmount, wallets, compact = false, className }: ProfitInfoPillProps) => {
  const { t } = useTranslation('common');

  const label = compact ? (
    <b className="font-bold">{userBalanceAmount}</b>
  ) : (
    <>
      {t('profit.balance')} <b className="font-bold text-content-primary">{userBalanceAmount}</b>
    </>
  );

  // Без кошельков раскрывать нечего — пилюля остаётся текстом. С одним кошельком
  // список всё равно открывается, как у wapclick: строка ведёт в Финансы с ним.
  if (wallets.length === 0) {
    return (
      <span
        className={cn(
          'flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full text-[13px] font-semibold leading-4',
          compact
            ? 'bg-surface-sidebar-hover px-3 py-2 text-content-inverse'
            : 'bg-surface-card px-[18px] py-2.5 text-content-secondary',
          className,
        )}
      >
        {label}
      </span>
    );
  }

  return (
    <PillDropdown
      className={className}
      label={label}
      heading={t('profit.wallets')}
      popupRole="menu"
      tone={compact ? 'inverse' : 'light'}
      size={compact ? 'compact' : 'default'}
    >
      {(close) =>
        wallets.map((w) => (
          <Link
            key={w.id}
            role="menuitem"
            to={`/payments?wallet_id=${w.id}`}
            className={pillDropdownItemClassName()}
            onClick={close}
          >
            {w.label}
          </Link>
        ))
      }
    </PillDropdown>
  );
};
